/**
 * Header-strip "ハイライト" controls for the migration-task pages. Two
 * dropdown multi-selects (領域 + 担当) that drive a *highlight* — not a
 * filter — so non-matching tasks dim instead of disappearing. See
 * {@link ../../lib/highlight.ts} for the URL-state contract.
 *
 * The component is presentation-only: it owns the open/close popover
 * state and renders option lists, but the actual `HighlightState` lives
 * in URL query params on the parent page. That makes reload + share
 * behaviour automatic without a context provider.
 *
 * Visual language matches the existing `WbsAreaFilter` chip rail — same
 * colour palette, same chip sizing — so the migration-task header
 * doesn't introduce a third "filter" idiom.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, X, Highlighter } from 'lucide-react';
import { areaBadgeClasses, type AreaColorIndexMap } from '../../lib/areaColor';
import { resolveAssigneeColorClasses } from '../../lib/assigneeColor';
import { cn } from '../../lib/utils';
import {
  EMPTY_HIGHLIGHT,
  isEmptyHighlight,
  toggleHighlightValue,
  UNASSIGNED_KEY,
  type HighlightState,
} from '../../lib/highlight';

export interface HighlightAssigneeOption {
  /** Either an assignee doc id or {@link UNASSIGNED_KEY}. */
  value: string;
  label: string;
  /** Stored colorIndex from the assignee doc (frozen at create). */
  colorIndex?: number;
}

export interface HighlightControlsProps {
  state: HighlightState;
  onChange: (next: HighlightState) => void;
  /** Project area catalog labels (display order). */
  areaOptions: readonly string[];
  areaColorIndices?: AreaColorIndexMap;
  /** Assignee options including the "(未割当)" sentinel where present. */
  assigneeOptions: readonly HighlightAssigneeOption[];
}

export function HighlightControls({
  state,
  onChange,
  areaOptions,
  areaColorIndices,
  assigneeOptions,
}: HighlightControlsProps): JSX.Element {
  const empty = isEmptyHighlight(state);
  const totalSelected = state.areaIds.length + state.assigneeIds.length;
  return (
    <div
      data-testid="highlight-controls"
      className="flex flex-wrap items-center gap-2 text-xs"
    >
      <span className="inline-flex items-center gap-1 text-slate-500">
        <Highlighter className="h-3.5 w-3.5" />
        ハイライト:
      </span>
      <HighlightDropdown
        testId="highlight-area"
        label="領域"
        emptyHint="領域を選択"
        selected={state.areaIds}
        onToggle={(value) =>
          onChange({
            ...state,
            areaIds: toggleHighlightValue(state.areaIds, value),
          })
        }
        renderTriggerExtras={(selected) =>
          selected.length === 0 ? null : (
            <span className="ml-1 inline-flex items-center gap-1">
              {selected.slice(0, 3).map((label) => (
                <span
                  key={label}
                  className={cn(
                    'inline-block rounded px-1 text-[10px]',
                    areaBadgeClasses(label, areaColorIndices),
                  )}
                >
                  {label}
                </span>
              ))}
              {selected.length > 3 ? (
                <span className="text-[10px] text-slate-500">
                  +{selected.length - 3}
                </span>
              ) : null}
            </span>
          )
        }
        options={areaOptions.map((label) => ({
          value: label,
          label,
          chipClass: areaBadgeClasses(label, areaColorIndices),
        }))}
      />
      <HighlightDropdown
        testId="highlight-assignee"
        label="担当"
        emptyHint="担当を選択"
        selected={state.assigneeIds}
        onToggle={(value) =>
          onChange({
            ...state,
            assigneeIds: toggleHighlightValue(state.assigneeIds, value),
          })
        }
        renderTriggerExtras={(selected) =>
          selected.length === 0 ? null : (
            <span className="ml-1 text-[10px] text-slate-500">
              ({selected.length})
            </span>
          )
        }
        options={assigneeOptions.map((opt) => ({
          value: opt.value,
          label: opt.label,
          chipClass:
            opt.value === UNASSIGNED_KEY
              ? 'bg-slate-100 text-slate-500 italic'
              : resolveAssigneeColorClasses(
                  opt.colorIndex != null
                    ? { colorIndex: opt.colorIndex }
                    : null,
                  opt.label,
                ),
        }))}
      />
      {!empty ? (
        <button
          type="button"
          data-testid="highlight-clear"
          onClick={() => onChange(EMPTY_HIGHLIGHT)}
          // Phase 2.3: tappable touch target on phones / tablets.
          className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 max-lg:min-h-[44px] max-lg:min-w-[44px] max-lg:px-2"
          title="ハイライトをクリア"
        >
          <X className="h-3 w-3" />
          クリア ({totalSelected})
        </button>
      ) : null}
    </div>
  );
}

interface DropdownOption {
  value: string;
  label: string;
  chipClass: string;
}

function HighlightDropdown({
  testId,
  label,
  emptyHint,
  selected,
  onToggle,
  options,
  renderTriggerExtras,
}: {
  testId: string;
  label: string;
  emptyHint: string;
  selected: readonly string[];
  onToggle: (value: string) => void;
  options: readonly DropdownOption[];
  renderTriggerExtras?: (selected: readonly string[]) => React.ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onDown);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = selected.length > 0;
  return (
    <div ref={ref} className="relative inline-block" data-testid={testId}>
      <button
        type="button"
        data-testid={`${testId}-trigger`}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition-colors',
          active
            ? 'border-slate-900 bg-slate-900 text-white'
            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400',
        )}
        aria-expanded={open}
      >
        <span>{label}</span>
        {active ? (
          <span className="rounded bg-white/20 px-1 text-[10px]">
            {selected.length}
          </span>
        ) : (
          <span className="text-[10px] text-slate-400">{emptyHint}</span>
        )}
        {renderTriggerExtras?.(selected)}
        <ChevronDown className="h-3 w-3 opacity-70" />
      </button>
      {open ? (
        <div
          className="absolute left-0 top-full z-30 mt-1 max-h-72 w-56 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
          data-testid={`${testId}-menu`}
        >
          {options.length === 0 ? (
            <div className="px-2 py-2 text-center text-[11px] text-slate-400">
              選択肢がありません
            </div>
          ) : (
            options.map((opt) => {
              const isSelected = selected.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`${testId}-option-${opt.value}`}
                  className={cn(
                    'flex w-full items-center gap-2 px-2 py-1 text-left text-xs hover:bg-slate-50',
                    isSelected ? 'bg-slate-100' : '',
                  )}
                  onClick={() => onToggle(opt.value)}
                >
                  <input
                    type="checkbox"
                    readOnly
                    checked={isSelected}
                    className="pointer-events-none"
                    aria-hidden
                  />
                  <span
                    className={cn(
                      'inline-block rounded px-1.5 py-0.5 text-[11px]',
                      opt.chipClass,
                    )}
                  >
                    {opt.label}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
